import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { jwtDecode } from 'jwt-decode';

/** ---------------------------------------------
 * FloatingToast: small, auto-dismiss tooltip/toast
 * type: 'success' | 'danger' | 'info' | 'warning'
 * --------------------------------------------- */
const FloatingToast = ({ show, type = 'success', message = '', onClose }) => {
  if (!show) return null;

  const bgMap = {
    success: 'bg-success text-white',
    danger: 'bg-danger text-white',
    info: 'bg-info text-dark',
    warning: 'bg-warning text-dark',
  };
  const iconMap = {
    success: 'bi-check-circle',
    danger: 'bi-x-circle',
    info: 'bi-info-circle',
    warning: 'bi-exclamation-triangle',
  };

  return (
    <div
      className="position-fixed top-0 end-0 p-3"
      style={{ zIndex: 1080 }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className={`shadow rounded-3 d-flex align-items-center gap-2 px-3 py-2 ${bgMap[type]}`}>
        <i className={`bi ${iconMap[type]} fs-5`}></i>
        <div className="small fw-medium">{message}</div>
        <button
          type="button"
          className="btn btn-sm btn-light ms-2"
          onClick={onClose}
          aria-label="Fermer la notification"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
    </div>
  );
};

const AccountPage = () => {
  const { token } = useAuth();
  const avatarInputRef = useRef();
  const coverInputRef = useRef();

  const sectionLabels = {
    latest_videos: "Dernières vidéos générées",
    trending: "Vidéos en tendance",
    creators: "Créateurs en vedette",
    activity: "Activité récente",
    challenges: "Défis de la communauté",
    suggestions: "Tu pourrais aimer..."
  };

  // mapping label <-> key
  const labelToKey = Object.fromEntries(
    Object.entries(sectionLabels).map(([k, v]) => [v, k])
  );

  // Toast state (replaces all old alerts)
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });
  const showToast = (type, message, delayMs = 2800) => {
    setToast({ show: true, type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(t => ({ ...t, show: false })), delayMs);
  };

  const [settings, setSettings] = useState({
    emailNotif: true,
    pushNotif: true,
    darkMode: true,
    language: 'fr',
    timezone: 'Europe/Paris',
    dashboardSections: Object.keys(sectionLabels), // store keys
    interests: ['Voyage', 'Culture', 'Nature', 'Histoire', 'Cuisine', 'Technologie', 'Écologie', 'Aventure']
  });

  const [newInterest, setNewInterest] = useState('');
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    display_name: '',
    handle: '',
    avatar_url: '',
    cover_url: '',
    bio: '',
    subscriptions: 0
  });
  const [editMode, setEditMode] = useState(false);
  const [media, setMedia] = useState([]);
  const [projectMedia, setProjectMedia] = useState([]);
  const [username, setUsername] = useState('');

  const [newMedia, setNewMedia] = useState({
    title: '',
    location: '',
    file: null,
    visibility: 'PUBLIC'
  });

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = jwtDecode(token);
      setUsername(decoded?.preferred_username || '');
    } catch (err) {
      console.error('Erreur lors du décodage du token:', err);
    }

    fetch("/myProfil", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        setProfile(data);
        setForm({
          display_name: data.display_name || '',
          handle: data.handle || '',
          avatar_url: data.avatar_url || '',
          cover_url: data.cover_url || '',
          bio: data.bio || '',
          subscriptions: data.subscriptions || 0
        });
      })
      .catch(() => {
        showToast('danger', "Impossible de charger le profil.");
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetch("/settings", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        let sections = data.dashboard_sections || [];
        // Normalize to keys
        if (Array.isArray(sections) && sections.length && typeof sections[0] === 'string' && !sectionLabels[sections[0]]) {
          sections = sections.map(label => labelToKey[label] || label);
        }
        setSettings(prev => ({
          ...prev,
          emailNotif: data.email_notifications === 1,
          pushNotif: data.push_notifications === 1,
          darkMode: data.dark_mode === 1,
          language: data.language || 'fr',
          timezone: data.timezone || 'Europe/Paris',
          dashboardSections: sections.length ? sections : Object.keys(sectionLabels),
          interests: Array.isArray(data.interests) && data.interests.length ? data.interests : prev.interests
        }));
      })
      .catch(() => {
        showToast('danger', "Impossible de charger les paramètres.");
      });
  }, [token]);

  useEffect(() => {
    if (!username || !token) return;

    fetch("/media/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setMedia(data);
      })
      .catch(() => { /* silencieux */ });

    fetch("/project-media/me", { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setProjectMedia(data);
      })
      .catch(() => { /* silencieux */ });
  }, [username, token]);

  const handleSave = () => {
    fetch("/myProfil", {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(form)
    })
      .then(res => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(() => {
        setEditMode(false);
        setProfile(prev => ({ ...prev, ...form }));
        window.dispatchEvent(new CustomEvent('profile-updated', {
          detail: {
            display_name: form.display_name,
            handle: form.handle,
            bio: form.bio
          }
        }));
        showToast('success', 'Profil mis à jour.');
      })
      .catch(() => showToast('danger', "Erreur lors de la mise à jour du profil."));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('avatar', file);
    fetch("/myProfil/avatar", {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(data => {
        setForm(prev => ({ ...prev, avatar_url: data.url }));
        setProfile(prev => ({ ...(prev || {}), avatar_url: data.url }));
        try {
          const kcUser = JSON.parse(localStorage.getItem('kc_user') || '{}');
          localStorage.setItem('kc_user', JSON.stringify({ ...kcUser, avatar_url: data.url }));
        } catch { }
        window.dispatchEvent(new CustomEvent('profile-updated', { detail: { avatar_url: data.url } }));
        showToast('success', 'Avatar mis à jour.');
      })
      .catch(() => showToast('danger', "Échec de l’envoi de l’avatar."));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const data = new FormData();
    data.append('cover', file);
    fetch("/myProfil/cover", {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: data
    })
      .then(res => res.json())
      .then(data => {
        setForm(prev => ({ ...prev, cover_url: data.url }));
        showToast('success', 'Couverture mise à jour.');
      })
      .catch(() => showToast('danger', "Échec de l’envoi de la couverture."));
  };

  const handleSaveSettings = () => {
    if (!token) return;
    const payload = {
      email_notifications: settings.emailNotif ? 1 : 0,
      push_notifications: settings.pushNotif ? 1 : 0,
      dark_mode: settings.darkMode ? 1 : 0,
      language: settings.language,
      timezone: settings.timezone,
      dashboard_sections: settings.dashboardSections, // already keys
      interests: settings.interests
    };
    fetch("/settings", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (res.ok) {
          showToast('success', "Préférences enregistrées.");
        } else {
          showToast('danger', "Erreur à l'enregistrement des préférences.");
        }
      })
      .catch(() => showToast('danger', "Erreur réseau pendant l'enregistrement."));
  };

  const handleUploadMedia = async (e) => {
    e?.preventDefault?.();

    if (!newMedia.file || !newMedia.title.trim() || !newMedia.location.trim()) {
      showToast('danger', "Merci de remplir tous les champs requis.");
      return;
    }

    const formData = new FormData();
    formData.append("media", newMedia.file);
    formData.append("title", newMedia.title.trim());
    formData.append("location", newMedia.location.trim());
    formData.append("visibility", newMedia.visibility || "PUBLIC");

    try {
      const response = await fetch("/upload", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        showToast('danger', result?.error || "Erreur lors de l'upload.");
        return;
      }

      setMedia(prev => [result, ...prev]);
      setNewMedia({ title: '', location: '', file: null, visibility: 'PUBLIC' });
      showToast('success', 'Média ajouté avec succès.');
    } catch {
      showToast('danger', "Erreur réseau ou serveur.");
    }
  };

  const renderVisibilityBadge = (visibility) => {
    const isPublic = visibility === 'PUBLIC';
    return (
      <span className={`badge px-3 py-1 rounded-pill fw-semibold ${isPublic ? 'bg-success text-light' : 'bg-warning text-dark'}`}>
        <i className={`bi ${isPublic ? 'bi-globe' : 'bi-lock-fill'} me-1`}></i>
        {isPublic ? 'Public' : 'Privé'}
      </span>
    );
  };

  const addInterest = () => {
    const v = (newInterest || '').trim();
    if (!v) {
      showToast('warning', "Veuillez saisir un centre d'intérêt.");
      return;
    }
    if (settings.interests.map(i => i.toLowerCase()).includes(v.toLowerCase())) {
      showToast('info', "Cet intérêt existe déjà.");
      return;
    }
    setSettings(prev => ({ ...prev, interests: [...prev.interests, v] }));
    setNewInterest('');
    showToast('success', "Centre d'intérêt ajouté.");
  };

  if (!profile) return <div className="p-5">Chargement...</div>;

  return (
    <div className="container py-4">

      {/* Floating toast */}
      <FloatingToast
        show={toast.show}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />

      {/* Couverture */}
      <div className="mb-4 position-relative rounded-4 overflow-hidden" style={{ paddingTop: '50px', height: '300px' }}>
        {form.cover_url && (
          <img src={`${form.cover_url}`} alt="cover" className="w-100 h-100 object-fit-cover" />
        )}
        <button
          className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-2"
          onClick={() => coverInputRef.current.click()}
        >
          <i className="bi bi-image-fill me-1"></i>
          Modifier la couverture
        </button>
        <input type="file" accept="image/*" hidden ref={coverInputRef} onChange={handleCoverChange} />
      </div>

      {/* Profil utilisateur */}
      <div className="card shadow-sm rounded-4 p-4 mb-5">
        <div className="d-flex align-items-center mb-3">
          <div onClick={() => avatarInputRef.current.click()} style={{ cursor: 'pointer' }}>
            <img
              src={form.avatar_url ? `${form.avatar_url}` : "https://via.placeholder.com/80?text=?"}
              alt="avatar"
              className="rounded-circle me-3 border"
              style={{ width: 80, height: 80, objectFit: 'cover' }}
            />
          </div>
          <input type="file" accept="image/*" hidden ref={avatarInputRef} onChange={handleAvatarChange} />
          <div className="flex-grow-1">
            {editMode ? (
              <>
                <input
                  className="form-control mb-2"
                  value={form.display_name}
                  onChange={e => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Nom d’affichage"
                />
                <input
                  className="form-control mb-2"
                  value={form.handle}
                  onChange={e => setForm({ ...form, handle: e.target.value })}
                  placeholder="@pseudo"
                />
              </>
            ) : (
              <>
                <h5 className="mb-0 fw-bold">{profile.display_name}</h5>
                <p className="text-muted mb-1">{profile.handle}</p>
                <p className="text-muted mb-0">Abonnements : {profile.subscriptions ?? 0}</p>
              </>
            )}
          </div>
          <div className="text-end">
            {editMode ? (
              <>
                <button className="btn btn-outline-secondary me-2" onClick={() => setEditMode(false)}>
                  <i className="bi bi-arrow-counterclockwise me-1"></i>Annuler
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  <i className="bi bi-save me-1"></i>Enregistrer
                </button>
              </>
            ) : (
              <button className="btn btn-outline-primary" onClick={() => setEditMode(true)}>
                <i className="bi bi-pencil-square me-1"></i>Modifier
              </button>
            )}
          </div>
        </div>

        <div className="mb-3">
          {editMode ? (
            <textarea
              className="form-control"
              rows="3"
              value={form.bio}
              onChange={e => setForm({ ...form, bio: e.target.value })}
              placeholder="Présentez-vous…"
            />
          ) : (
            <p className="text-muted">{profile.bio}</p>
          )}
        </div>
      </div>

      {/* Centres d'intérêt */}
      <h2 className="mb-3">Centres d'intérêt</h2>

      <div className="row g-2 align-items-center mb-3">
        <div className="col-12 col-md-6">
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-heart"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Ajouter un centre d’intérêt (ex: Architecture)"
              value={newInterest}
              onChange={(e) => setNewInterest(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addInterest(); }}
            />
            <button className="btn btn-primary" onClick={addInterest} title="Ajouter">
              <i className="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label">
          Cliquez pour sélectionner/désélectionner vos centres d'intérêt
        </label>
        <div className="d-flex flex-wrap gap-2">
          {settings.interests.map((interest, idx) => {
            const selected = settings.interests.includes(interest);
            return (
              <button
                key={`${interest}-${idx}`}
                type="button"
                className={`btn rounded-pill px-3 py-1 border ${selected ? 'btn-primary text-white' : 'btn-outline-secondary'}`}
                onClick={() => {
                  setSettings(prev => {
                    const already = prev.interests.includes(interest);
                    const next = already
                      ? prev.interests.filter(i => i !== interest)
                      : [...prev.interests, interest];
                    return { ...prev, interests: next };
                  });
                }}
                style={{ fontSize: '0.9rem' }}
              >
                <i className={`bi ${selected ? 'bi-check2-circle' : 'bi-circle'} me-1`}></i>
                {interest}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sections tableau de bord */}
      <h2 className="mb-4">Sections à afficher dans le tableau de bord</h2>

      <div className="mb-4">
        {Object.entries(sectionLabels).map(([key, label]) => (
          <div className="form-check form-switch" key={key}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`section-${key}`}
              checked={settings.dashboardSections.includes(key)}
              onChange={() => {
                setSettings(prev => {
                  const next = prev.dashboardSections.includes(key)
                    ? prev.dashboardSections.filter(s => s !== key)
                    : [...prev.dashboardSections, key];
                  return { ...prev, dashboardSections: next };
                });
              }}
            />
            <label className="form-check-label" htmlFor={`section-${key}`}>
              <i className="bi bi-layout-three-columns me-1"></i>{label}
            </label>
          </div>
        ))}
      </div>

      <button className="btn btn-primary" onClick={handleSaveSettings}>
        <i className="bi bi-cloud-check me-1"></i>Sauvegarder les préférences
      </button>

      <hr className="my-5" />

    

    </div>
  );
};

export default AccountPage;
